<template>
  <v-dialog v-model="addDialog" class="the-app-dialogs" persistent max-width="900px">
    <v-card>
      <form @submit.prevent="validateBeforeSubmit($event)">
        <v-layout row class="no-select" style="background-color:#09132f">
          <h3 id="dialog-title" class="text-uppercase px-3 pt-3" style="text-xs-center letter-spacing:2px">
            <v-layout align-center>
              <v-icon light x-large class="rgba(0,0,0,0.3)--text pb-1">fa-dolly</v-icon>
              <span class="px-3">{{ $t('Add New Mixture') }}</span>
            </v-layout>
          </h3>
          <v-spacer />
          <v-card-actions>
            <v-btn fab icon dark @click="$emit('hidemodal')">
              <v-icon>close</v-icon>
            </v-btn>
          </v-card-actions>
        </v-layout>

        <v-card-title class="grey lighten-3">
          <v-layout row wrap>
            <v-flex xs12>
              <v-layout row wrap justify-content-end>
                <v-flex xs6 pa-2>
                  <v-text-field
                    id="mixture-focus-me"
                    v-model="form.name"
                    :label="`${$t('Mixture Name')} *`"
                    name="mixture_name"
                    hide-details
                    box
                    background-color="white"
                    :error="errors.has('mixture_name')"
                    v-validate="'required'"
                  />
                </v-flex>

                <v-flex xs3 pa-2>
                  <v-text-field
                    v-model="form.product_weight"
                    :label="`${$t('Product Weight')} *`"
                    name="product_weight"
                    hide-details
                    box
                    background-color="white"
                    :error="errors.has('product_weight')"
                    v-validate="'required'"
                    @input="changeEstimate"
                  />
                </v-flex>

                <v-flex xs3 pa-2>
                  <v-text-field
                    v-model="form.estimated_product_quantity"
                    :label="`${$t('Production Quantity')} *`"
                    name="estimated_product_quantity"
                    hide-details
                    box
                    readonly
                    background-color="white"
                    :error="errors.has('estimated_product_quantity')"
                    v-validate="'required'"
                  />
                </v-flex>
              </v-layout>
            </v-flex>

            <v-flex xs12 style="margin-top: 20px">
              <v-layout row wrap class="form-table-header">
                <v-flex xs1 pa-2 class="form-table-content">#</v-flex>
                <v-flex pa-2 class="form-table-content">{{ $t('Raw Material') }}</v-flex>
                <v-flex xs1 class="form-table-content" v-if="hasPermissions(['raw_materials_store'])">
                  <v-tooltip top>
                    <v-btn label @click="$emit('add-raw-material')" small slot="activator" icon color="green">
                      <v-icon color="white">add</v-icon>
                    </v-btn>
                    <span>{{ $t('Add new row material') }}</span>
                  </v-tooltip>
                </v-flex>

                <v-flex xs2 pa-2 class="form-table-content">{{ $t('Quantity') }}</v-flex>
                <v-flex xs1 class="form-table-content">
                  <v-tooltip top>
                    <v-btn label @click="addRow()" small slot="activator" icon color="green">
                      <v-icon color="white">add</v-icon>
                    </v-btn>
                    <span>{{ $t('Add new row') }}</span>
                  </v-tooltip>
                </v-flex>
              </v-layout>

              <template v-if="rowNumbers > 0">
                <v-layout row wrap v-for="(row, index) in rowNumbers" :key="index">
                  <v-flex xs1 class="pa-1" style="text-align: center; margin-top: 20px; font-size: 16px">{{
                    index + 1
                  }}</v-flex>
                  <v-flex xs8 class="pa-1">
                    <v-autocomplete
                      v-model="product_id_fk[index]"
                      :label="`${$t('Raw Materials')} *`"
                      :items="productsList"
                      hide-details
                      box
                      :data-vv-name="'raw_material' + (index + 1)"
                      background-color="white"
                      :error="errors.has('raw_material' + (index + 1))"
                      v-validate="'required'"
                    />
                  </v-flex>
                  <v-flex xs2 class="pa-1">
                    <v-text-field
                      v-model.number="quantity[index]"
                      :label="`${$t('Quantity')} *`"
                      hide-details
                      box
                      :data-vv-name="'quantity' + (index + 1)"
                      background-color="white"
                      :error="errors.has('quantity' + (index + 1))"
                      v-validate="'required|decimal:3|min_value:0'"
                    />
                  </v-flex>

                  <v-flex
                    xs1
                    :class="index + 1 === rowNumbers && index + 1 > 1 ? 'pt-3' : 'pa-3'"
                    style="text-align: center"
                  >
                    <v-tooltip top>
                      <v-btn label flat @click="removeRow(index)" small slot="activator" icon color="error">
                        <v-icon>delete</v-icon>
                      </v-btn>
                      <span>{{ $t('Remove this row') }}</span>
                    </v-tooltip>
                    <v-tooltip top v-if="index + 1 === rowNumbers && index + 1 > 1">
                      <v-btn label @click="addRow()" small slot="activator" icon flat>
                        <v-icon size="28" color="green">add_circle</v-icon>
                      </v-btn>
                      <span>{{ $t('Add new row') }}</span>
                    </v-tooltip>
                  </v-flex>
                </v-layout>
              </template>
            </v-flex>
          </v-layout>
        </v-card-title>
        <v-divider light />

        <v-card-actions class="grey lighten-3">
          <v-layout column>
            <v-flex xs12 class="pb-2 px-2" style="color: #444;"
              >- {{ $t('Fields marked with asterisk ( * ) are required.') }}</v-flex
            >

            <v-layout justify-end align-center pr-2 pl-2>
              <v-checkbox color="primary" v-model="keepForm" :label="$t('Keep this form to add another one.')" />

              <v-btn outline color="primary" @click="$emit('hidemodal')">{{ $t('Cancel') }}</v-btn>
              <v-btn type="submit" color="success" :disabled="rowNumbers < 1" :loading="btnAddLoading">
                <v-icon :right="$vuetify.rtl" :left="!$vuetify.rtl">save</v-icon>
                {{ $t('Add') }}
              </v-btn>
            </v-layout>
          </v-layout>
        </v-card-actions>
      </form>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import * as helpers from '@/mixins/helperFunctions.js'

export default {
  name: 'add-component',

  props: {
    addDialog: { type: Boolean, required: true }
  },

  data() {
    return {
      btnAddLoading: false,
      form: {
        product_weight: 0,
        estimated_product_quantity: 0
      },
      product_id_fk: [],
      quantity: [0],
      rowNumbers: 1,
      keepForm: false
    }
  },

  computed: {
    ...mapGetters({
      baseUrls: 'misc/getBaseUrls',
      productsList: 'rawMaterials/getFullRawMaterialsList'
    })
  },

  watch: {
    addDialog: {
      immediate: false,
      handler() {
        if (this.addDialog) {
          this.addEscKeyListener()
          this.resetForm()
          this.keepForm = false
        }
      }
    },

    quantity: {
      immediate: true,
      handler() {
        this.changeEstimate()
      }
    }
  },

  methods: {
    ...mapActions({
      addMixture: 'mixtures/addMixture'
    }),

    hasPermissions(permissions) {
      return helpers.userHasPermissions(permissions)
    },

    addEscKeyListener() {
      document.addEventListener('keyup', e => {
        // if the "ESC" button was pressed and this dialog is shown, hide it
        if (e.keyCode === 27 && this.addDialog) {
          this.$emit('hidemodal')
        }
      })
    },

    resetForm() {
      this.$validator.pause()
      for (let key in this.form) {
        this.form[`${key}`] = null
      }
      this.product_id_fk = []
      this.quantity = [0]
      this.rowNumbers = 1
      this.form.estimated_product_quantity = 0
      this.$nextTick(() => {
        this.$validator.resume()
        // default veutify autofocus only one time works so we have to handled in this way.
        // document.getElementById('mixture-focus-me').focus()
      })
    },

    changeEstimate() {
      const total = this.quantity.reduce((a, b) => a + b)
      this.form.estimated_product_quantity = (total / this.form.product_weight).toFixed(2)
    },

    addRow() {
      this.rowNumbers = this.rowNumbers + 1
    },

    removeRow(index) {
      this.rowNumbers = this.rowNumbers - 1
      this.product_id_fk.splice(index, 1)
      this.quantity.splice(index, 1)
    },

    validateBeforeSubmit(event) {
      this.$validator
        .validateAll()
        .then(() => {
          if (!this.errors.any()) {
            this.btnAddLoading = true

            let formData = new FormData(event.target)

            this.product_id_fk.forEach((p, index) => {
              formData.append('raw_material_id_fk[' + [index] + ']', p)
            })

            this.quantity.forEach((q, index) => {
              formData.append('mixture_quantity[' + [index] + ']', q)
            })

            this.addMixture(formData)
              .then(() => {
                this.btnAddLoading = false
                this.product_id_fk = []
                this.damaged_quantity = []
                this.$emit('reset-datatable')
                if (this.keepForm) {
                  this.resetForm()
                } else {
                  this.$emit('hidemodal')
                }
              })
              .catch(() => {
                this.btnAddLoading = false
              })
          }
        })
        .catch(() => {
          this.btnAddLoading = false
        })
    }
  }
}
</script>

<style scoped />
